/**
 * Email Provider Templates
 * Pre-configured settings for popular email providers
 * Users just need to enter email and password
 */

export const EMAIL_PROVIDERS = {
  gmail: {
    name: 'Gmail',
    logo: '📧',
    description: 'Google Gmail',
    providers: ['SMTP', 'IMAP'],
    color: '#EA4335',
    smtp: {
      host: 'smtp.gmail.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://myaccount.google.com/apppasswords',
    },
    imap: {
      host: 'imap.gmail.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    youtubeLink: 'https://www.youtube.com/watch?v=0K6eZGS5PWc',
    steps: [
      'Enable 2-Factor Authentication on your Google Account',
      'Visit https://myaccount.google.com/apppasswords',
      'Select "Mail" and "Windows Computer"',
      'Google generates a 16-character password',
      'Copy that password and paste it here',
      'Use your Gmail address as username',
      'Click Test to verify connection'
    ],
    guide: `
📧 Gmail Setup Guide:

Step 1: Enable 2-Factor Authentication
• Go to https://myaccount.google.com/security
• Enable 2-Step Verification if not already enabled

Step 2: Generate App Password
• Visit https://myaccount.google.com/apppasswords
• Select "Mail" from dropdown
• Select "Windows Computer" (or your device)
• Click "Generate"
• Google creates a 16-character password

Step 3: Add to App
• Copy the generated password (do NOT use your regular Gmail password)
• Paste it in the Password field above
• Use your Gmail email as Username
• Click "Test" button to verify
• If successful, click "Add Account"

⚠️ Important: You MUST use the app password, not your regular Gmail password!
If you haven't enabled 2FA, this won't work. Enable it first.

📺 Watch Tutorial: https://www.youtube.com/watch?v=L4jnNYvPr3E

✅ Once added, you can:
• Send emails from this account
• Receive emails in the Inbox
• Track email opens
• Schedule campaigns
    `.trim(),
  },

  outlook: {
    name: 'Outlook / Hotmail',
    logo: '💙',
    description: 'Microsoft Outlook',
    providers: ['SMTP', 'IMAP'],
    color: '#0078D4',
    smtp: {
      host: 'smtp-mail.outlook.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: false,
    },
    imap: {
      host: 'imap-mail.outlook.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    youtubeLink: 'https://www.youtube.com/watch?v=OXe0R_7xP7w',
    steps: [
      'Use your regular Outlook email and password',
      'Enable IMAP in account settings',
      'If 2FA is enabled, create an app password',
      'Add your email in the Username field',
      'Click Test to verify connection'
    ],
    guide: `
💙 Outlook Setup Guide:

Step 1: Check IMAP is Enabled
• Go to https://account.microsoft.com/security
• Click "Advanced security options"
• Look for "IMAP" settings and enable if needed

Step 2: Use Your Regular Password
• Use your Outlook email address as Username
• Use your regular Outlook password
• If you have 2FA enabled, use an App Password instead:
  https://account.microsoft.com/security

Step 3: Create App Password (If 2FA is on)
• Go to https://account.microsoft.com/security
• Click "App passwords" 
• Select "Other (custom)"
• Copy the generated password
• Use that instead of your regular password

Step 4: Add to App
• Paste your email in Username field
• Paste your password (or app password)
• Click "Test" button
• If successful, click "Add Account"

📺 Watch Tutorial: https://www.youtube.com/watch?v=OXe0R_7xP7w

✅ Once added, you can:
• Send emails from your Outlook account
• Receive emails in the Inbox
• Track email opens
• Manage campaigns
    `.trim(),
  },

  yahoo: {
    name: 'Yahoo Mail',
    logo: '📬',
    description: 'Yahoo Mail',
    providers: ['SMTP', 'IMAP'],
    color: '#6001D2',
    smtp: {
      host: 'smtp.mail.yahoo.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://login.yahoo.com/account/security',
    },
    imap: {
      host: 'imap.mail.yahoo.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    youtubeLink: 'https://www.youtube.com/watch?v=1unBf_5bFS4',
    steps: [
      'Visit https://login.yahoo.com/account/security',
      'Generate an App Password',
      'Select "Mail" and "Other App"',
      'Copy the generated password',
      'Use your Yahoo email as Username',
      'Click Test to verify'
    ],
    guide: `
📬 Yahoo Mail Setup Guide:

⚠️ Important: Yahoo REQUIRES App Passwords

Step 1: Generate App Password
• Go to https://login.yahoo.com/account/security
• Click "Generate app password"
• Select "Mail" from first dropdown
• Select "Other App" from second dropdown
• Type a name (e.g., "My Email App")
• Click "Generate"

Step 2: Copy Your App Password
• Yahoo generates a password like: abc de fgh ijk lm
• Copy this entire password
• DO NOT use your regular Yahoo password

Step 3: Add to App
• Username: Your complete Yahoo email address
• Password: The app password you just generated
• Click "Test" button
• If successful, click "Add Account"

📺 Watch Tutorial: https://www.youtube.com/watch?v=wbT1VYmOl9w

✅ Once added, you can:
• Send emails from your Yahoo account
• Receive emails in the Inbox
• Track campaigns
• Manage multiple accounts
    `.trim(),
  },

  office365: {
    name: 'Office 365',
    logo: '🌐',
    description: 'Microsoft Office 365',
    providers: ['SMTP', 'IMAP'],
    color: '#0078D4',
    smtp: {
      host: 'smtp.office365.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: false,
    },
    imap: {
      host: 'outlook.office365.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    youtubeLink: 'https://www.youtube.com/watch?v=BxjDJXz0_bQ',
    steps: [
      'Use your Office 365 email and password',
      'Enable IMAP if needed',
      'If 2FA is enabled, create app password',
      'Add your email in Username field',
      'Click Test to verify'
    ],
    guide: `
🌐 Office 365 Setup Guide:

Step 1: Enable IMAP
• Go to https://account.microsoft.com
• Click "Advanced security options"
• Enable IMAP if needed

Step 2: Use Your Email and Password
• Username: Your Office 365 email
• Password: Your Office 365 password
• OR if 2FA is enabled, use App Password:
  https://account.microsoft.com/security

Step 3: Add to App
• Enter your Office 365 email
• Enter your password (or app password)
• Click "Test" button
• If successful, click "Add Account"

📺 Watch Tutorial: https://www.youtube.com/watch?v=Mu0T8r7vl4Q

✅ Once added, you can use all email features
    `.trim(),
  },

  sendgrid: {
    name: 'SendGrid',
    logo: '🚀',
    description: 'SendGrid Email API',
    providers: ['SMTP'],
    youtubeLink: 'https://www.youtube.com/watch?v=gJ-lNH5aMCQ',
    smtp: {
      host: 'smtp.sendgrid.net',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://app.sendgrid.com/settings/api_keys',
    },
    guide: `
🚀 SendGrid Setup Guide:

Step 1: Create SendGrid Account
• Go to https://sendgrid.com
• Sign up for free account
• Verify email

Step 2: Create API Key
• Go to https://app.sendgrid.com/settings/api_keys
• Click "Create API Key"
• Name it (e.g., "My Email App")
• Select "Full Access" or "Mail Send"
• Copy the API key

Step 3: Add to App
• Username: apikey (exactly - lowercase)
• Password: [Paste your API key]
• From Email: verified-sender@domain.com
• Click "Test"
• Once verified, click "Add Account"

💡 Tip: Use for bulk emails and campaigns

📺 Watch Tutorial: https://www.youtube.com/watch?v=VTWNTjJZN5w

✅ Ready to send marketing emails!
    `.trim(),
  },

  mailgun: {
    name: 'Mailgun',
    logo: '📨',
    description: 'Mailgun Email Service',
    providers: ['SMTP'],    youtubeLink: 'https://www.youtube.com/watch?v=x-Cf-QVuTEo',    smtp: {
      host: 'smtp.mailgun.org',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://app.mailgun.com/app/account/security/api_keys',
    },
    guide: `
📨 Mailgun Setup Guide:

Step 1: Create Mailgun Account
• Go to https://mailgun.com
• Sign up and verify email

Step 2: Add and Verify Domain
• Add your domain in Mailgun
• Add DNS records (shown in dashboard)
• Wait for domain verification

Step 3: Get SMTP Credentials
• Go to https://app.mailgun.com/app/account/security/api_keys
• Copy your SMTP credentials
• Username: postmaster@yourdomain.com
• Password: Your SMTP password

Step 4: Add to App
• Paste username and password
• SMTP Host: smtp.mailgun.org
• SMTP Port: 587
• Click "Test"
• Once verified, click "Add Account"

📺 Watch Tutorial: https://www.youtube.com/watch?v=ZeGV-LN8vA0

✅ Ready for bulk email campaigns!
    `.trim(),
  },

  sparkpost: {
    name: 'SparkPost',
    logo: '⚡',
    description: 'SparkPost Email Service',
    providers: ['SMTP'],
    youtubeLink: 'https://www.youtube.com/watch?v=NhKnXDW8vDI',
    smtp: {
      host: 'smtp.sparkpostmail.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://app.sparkpost.com/account/credentials',
    },
    guide: `
⚡ SparkPost Setup Guide:

Step 1: Create SparkPost Account
• Go to https://www.sparkpost.com
• Sign up for account
• Verify email

Step 2: Add Sending Domain
• Go to Sending Domains
• Add your domain
• Add DNS records (DKIM, SPF)
• Verify domain

Step 3: Get API Token
• Go to https://app.sparkpost.com/account/credentials
• Create API Key
• Select "SMTP Relay" permission
• Copy the API token

Step 4: Add to App
• Username: SMTP_Injection (exactly - no change!)
• Password: [Paste your API token]
• SMTP Host: smtp.sparkpostmail.com
• SMTP Port: 587
• Click "Test"

📺 Watch Tutorial: https://www.youtube.com/watch?v=KN9hOTXfgvw

✅ High-volume email sending ready!
    `.trim(),
  },

  aws_ses: {
    name: 'AWS SES',
    logo: '☁️',
    description: 'Amazon SES',
    providers: ['SMTP'],    youtubeLink: 'https://www.youtube.com/watch?v=MU0WczJ_Sn8',    smtp: {
      host: 'email-smtp.us-east-1.amazonaws.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://console.aws.amazon.com/ses/',
    },
    guide: `
AWS SES Setup Guide:
1. Create an AWS account
2. Request SES production access (verify your domain/email)
3. Create SMTP credentials in SES Console
4. Username: Your SMTP username from AWS
5. Password: Your SMTP password from AWS
6. Update Host based on your region (e.g., eu-west-1, ap-southeast-1)
7. Test the connection
    `.trim(),
  },

  postmark: {
    name: 'Postmark',
    logo: '📮',
    description: 'Postmark Email API',
    providers: ['SMTP'],    youtubeLink: 'https://www.youtube.com/watch?v=cN-RVbr46A4',    smtp: {
      host: 'smtp.postmarkapp.com',
      port: 587,
      secure: true, // TLS
      requiresAppPassword: true,
      appPasswordUrl: 'https://account.postmarkapp.com/servers',
    },
    guide: `
Postmark Setup Guide:
1. Create a Postmark account
2. Verify your sender signature (domain or email)
3. Get your Server API token
4. Username: "postmark" (exactly)
5. Password: Your Server API token
6. From Email: Your verified Postmark sender
7. Test the connection
    `.trim(),
  },

  titan: {
    name: 'Titan Email',
    logo: '🔥',
    description: 'Professional business email',
    providers: ['SMTP', 'IMAP'],
    youtubeLink: 'https://www.youtube.com/watch?v=DH-cJ8P8aAE',
    smtp: {
      host: 'smtp.titan.email',
      port: 587,
      secure: true,
    },
    imap: {
      host: 'imap.titan.email',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
🔥 Titan Email Setup:

Step 1: Get Your Credentials
• Log in to Titan Email
• Go to Account Settings
• Your email and password

Step 2: Add to App
• Email: your-email@yourdomain.com
• Password: Your Titan password
• SMTP Host: smtp.titan.email
• SMTP Port: 587 (TLS)
• IMAP Host: imap.titan.email
• IMAP Port: 993

Step 3: Test Connection
• Click Test button
• If successful (✓), click Add Account

📺 Video: https://www.youtube.com/watch?v=LxEP41Rw33w
    `.trim(),
  },

  hostinger: {
    name: 'Hostinger Email',
    logo: '🌐',
    description: 'Email hosting with Hostinger',
    providers: ['SMTP', 'IMAP'],
    youtubeLink: 'https://www.youtube.com/watch?v=n8R0jbB_Bco',
    smtp: {
      host: 'smtp.hostinger.com',
      port: 465,
      secure: true,
    },
    imap: {
      host: 'imap.hostinger.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
🌐 Hostinger Email Setup:

Step 1: Get Your Details
• Log in to Hostinger
• Go to Email section
• Your email and password

Step 2: Add to App
• Email: your-email@yourdomain.com
• Password: Your email password
• SMTP Host: smtp.hostinger.com
• SMTP Port: 465 (SSL)
• IMAP Host: imap.hostinger.com
• IMAP Port: 993

Step 3: Test Connection
• Click Test button
• If successful (✓), click Add Account

📺 Tutorial: https://www.youtube.com/watch?v=EKO4OX0-UMI
    `.trim(),
  },

  namecheap: {
    name: 'Namecheap Email',
    logo: '🏷️',
    description: 'Private Email with Namecheap',
    providers: ['SMTP', 'IMAP'],
    youtubeLink: 'https://www.youtube.com/watch?v=w5cZjqxPfv0',
    smtp: {
      host: 'mail.privateemail.com',
      port: 587,
      secure: true,
    },
    imap: {
      host: 'mail.privateemail.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
🏷️ Namecheap Private Email Setup:

Step 1: Access Your Account
• Log in to Namecheap
• Go to Email section
• Your email and password

Step 2: Add to App
• Email: your-email@yourdomain.com
• Password: Your email password
• SMTP Host: mail.privateemail.com
• SMTP Port: 587 (TLS)
• IMAP Host: mail.privateemail.com
• IMAP Port: 993

Step 3: Test Connection
• Click Test button
• If successful (✓), click Add Account

📺 Help: https://www.youtube.com/watch?v=nqTbAGe8BoI
    `.trim(),
  },

  zoho: {
    name: 'Zoho Mail',
    logo: '✉️',
    description: 'Professional Zoho Mail',
    providers: ['SMTP', 'IMAP'],
    youtubeLink: 'https://www.youtube.com/watch?v=Sd5jBhCa9Vs',
    smtp: {
      host: 'smtp.zoho.com',
      port: 587,
      secure: true,
      requiresAppPassword: true,
    },
    imap: {
      host: 'imap.zoho.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
✉️ Zoho Mail Setup:

Step 1: Generate App Password
• Log in to Zoho Mail
• Go to Security settings
• Create app-specific password
• Copy the password

Step 2: Add to App
• Email: your-email@yourdomain.com
• Password: App-specific password (NOT regular password)
• SMTP Host: smtp.zoho.com
• SMTP Port: 587 (TLS)
• IMAP Host: imap.zoho.com
• IMAP Port: 993

Step 3: Test Connection
• Click Test button
• If successful (✓), click Add Account

⚠️ Use app-specific password, not regular password!

📺 Video: https://www.youtube.com/watch?v=dJx3VXMkQQ8
    `.trim(),
  },

  protonmail: {
    name: 'ProtonMail',
    logo: '🔐',
    description: 'Secure encrypted email',
    providers: ['SMTP', 'IMAP'],
    youtubeLink: 'https://www.youtube.com/watch?v=CnyJr6BjAvc',
    smtp: {
      host: 'smtp.protonmail.com',
      port: 587,
      secure: true,
      requiresAppPassword: true,
    },
    imap: {
      host: 'imap.protonmail.com',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
🔐 ProtonMail Setup (Advanced):

⚠️ ProtonMail requires ProtonMail Bridge

Step 1: Download Bridge
• Visit: https://protonmail.com/bridge/
• Download and install Bridge
• Run Bridge

Step 2: Add to App
• SMTP Host: localhost
• SMTP Port: 1025
• IMAP Host: localhost
• IMAP Port: 1143
• Email and password: Your ProtonMail credentials

Step 3: Keep Bridge Running
• ProtonMail Bridge must stay running
• Start on system boot

📺 Setup: https://www.youtube.com/watch?v=8KOkwc6WCqQ
    `.trim(),
  },

  godaddy: {
    name: 'GoDaddy Email',
    logo: '🦎',
    description: 'Email hosting with GoDaddy',
    providers: ['SMTP', 'IMAP'],    youtubeLink: 'https://www.youtube.com/watch?v=j3S7e4Z1-K8',    smtp: {
      host: 'smtp.secureserver.net',
      port: 465,
      secure: true,
    },
    imap: {
      host: 'imap.secureserver.net',
      port: 993,
      secure: true,
      mailbox: 'INBOX',
    },
    guide: `
🦎 GoDaddy Email Setup:

Step 1: Get Your Email Details
• Log in to GoDaddy
• Go to Email section
• Note your email address and password

Step 2: Enable IMAP (Important!)
• Go to GoDaddy Email settings
• Enable IMAP access (usually enabled by default)
• Ensure "Less secure apps" are allowed if needed

Step 3: Add to App
• Email: your-email@yourdomain.com
• Password: Your GoDaddy email password
• SMTP Host: smtp.secureserver.net
• SMTP Port: 465
• IMAP Host: imap.secureserver.net
• IMAP Port: 993
• Security: SSL (enabled)

Step 4: Test Connection
• Click Test button
• If successful (✓), click Add Account

💡 Tip: GoDaddy email works great with any domain
• yourname@yourdomain.com
• Custom branding
• Professional email address

📺 Setup Video: https://www.youtube.com/watch?v=L4jnNYvPr3E
    `.trim(),
  },

  custom: {
    name: 'Custom SMTP',
    logo: '⚙️',
    description: 'Any Email Provider',
    providers: ['SMTP'],
    youtubeLink: 'https://www.youtube.com/watch?v=Q9gdLHuXJaY',
    guide: `
⚙️ Custom SMTP Setup:

Step 1: Get Your Provider Settings
• Contact your email provider
• Ask for SMTP host and port
• Ask for IMAP host and port (if receiving emails)
• Ask if TLS/SSL is required

Common providers:
• GoDaddy
• Bluehost
• 1&1
• Your hosting provider's support

Step 2: Enter Settings Manually
• SMTP Host: [from provider]
• SMTP Port: [usually 587 or 465]
• TLS/SSL: [check with provider]
• IMAP Host: [if receiving emails]
• IMAP Port: [usually 993]

Step 3: Test Connection
• Click Test button
• If successful (✓), click Add Account

💡 Tip: Search "[provider name] SMTP settings" on Google

📺 Tutorial: https://www.youtube.com/results?search_query=custom+smtp+setup+email
    `.trim(),
  }
};

/**
 * Get provider template by email domain
 * Automatically detects provider based on email address
 */
export const detectProviderByEmail = (email = '') => {
  const domain = email.toLowerCase().split('@')[1] || '';

  if (!domain) return null;

  // Gmail
  if (domain.includes('gmail.com') || domain.includes('googlemail.com')) {
    return EMAIL_PROVIDERS.gmail;
  }

  // Outlook
  if (
    domain.includes('outlook.com') ||
    domain.includes('outlook.co') ||
    domain.includes('live.com') ||
    domain.includes('hotmail.com')
  ) {
    return EMAIL_PROVIDERS.outlook;
  }

  // Yahoo
  if (
    domain.includes('yahoo.com') ||
    domain.includes('ymail.com') ||
    domain.includes('rocketmail.com')
  ) {
    return EMAIL_PROVIDERS.yahoo;
  }

  // Office 365
  if (domain.includes('onmicrosoft.com')) {
    return EMAIL_PROVIDERS.office365;
  }

  return null;
};

/**
 * Apply provider template to account form
 * Fills in SMTP/IMAP settings based on provider
 */
export const applyProviderTemplate = (email = '', provider = null) => {
  const template = provider || detectProviderByEmail(email);

  if (!template) return null;

  return {
    host: template.smtp?.host || '',
    port: template.smtp?.port || 587,
    secure: template.smtp?.secure !== false,
    imapHost: template.imap?.host || '',
    imapPort: template.imap?.port || 993,
    imapSecure: template.imap?.secure !== false,
    mailbox: template.imap?.mailbox || 'INBOX',
    popHost: '',
    popPort: 995,
    popSecure: true,
  };
};

/**
 * Get help text for a provider
 */
export const getProviderHelp = (providerId = '') => {
  const provider = EMAIL_PROVIDERS[providerId.toLowerCase()];
  return provider?.guide || '';
};

/**
 * Get all providers as options
 */
export const getProviderOptions = () => {
  return [
    // Popular - Auto-Detect
    { id: 'gmail', label: '📧 Gmail', category: 'Popular', color: '#EA4335' },
    { id: 'outlook', label: '💙 Outlook', category: 'Popular', color: '#0078D4' },
    { id: 'yahoo', label: '📬 Yahoo', category: 'Popular', color: '#410093' },
    
    // Business Email
    { id: 'office365', label: '🌐 Office 365', category: 'Business', color: '#0078D4' },
    { id: 'zoho', label: '✉️ Zoho Mail', category: 'Business', color: '#1F73E6' },
    { id: 'protonmail', label: '🔐 ProtonMail', category: 'Business', color: '#6D4AFF' },
    
    // Domain Email
    { id: 'titan', label: '🔥 Titan Email', category: 'Domain', color: '#FF6B35' },
    { id: 'hostinger', label: '🌐 Hostinger', category: 'Domain', color: '#2D3E50' },
    { id: 'namecheap', label: '🏷️ Namecheap', category: 'Domain', color: '#DE3E45' },
    { id: 'godaddy', label: '🦎 GoDaddy', category: 'Domain', color: '#317DFF' },
    
    // Transactional
    { id: 'sendgrid', label: '🚀 SendGrid', category: 'Transactional', color: '#1F73E6' },
    { id: 'mailgun', label: '📨 Mailgun', category: 'Transactional', color: '#CF2E2E' },
    { id: 'sparkpost', label: '⚡ SparkPost', category: 'Transactional', color: '#FA5C38' },
    
    // Custom
    { id: 'custom', label: '⚙️ Manual/Custom', category: 'Custom', color: '#757575' },
  ];
};

/**
 * Validation helper
 */
export const validateAccountForm = (form) => {
  const errors = [];

  if (!form.label?.trim()) {
    errors.push('Account label is required');
  }

  if (!form.fromEmail?.trim() || !form.fromEmail.includes('@')) {
    errors.push('Valid email address required');
  }

  if (!form.host?.trim()) {
    errors.push('SMTP host is required');
  }

  if (!form.port || form.port < 1 || form.port > 65535) {
    errors.push('SMTP port must be between 1 and 65535');
  }

  if (!form.username?.trim()) {
    errors.push('Username/email is required');
  }

  if (!form.password?.trim()) {
    errors.push('Password is required');
  }

  return errors;
};
